import type { OverlayEvent, OverlayEventType } from '@shared/types'

import type { EffectPlugin } from './types'

import { isNewerVersion } from './semver'

/**
 * Синглтон-реестр плагинов эффектов с версионированием.
 *
 * Правила версионирования:
 *  - Встроенные плагины регистрируются с version "0.0.0"
 *  - Динамический плагин с version "1.0.0" перезапишет встроенный
 *  - Повторная регистрация той же версии — игнорируется (idempotent)
 *  - Понижение версии ("downgrade") — запрещено, используй forceRegister
 */
class EffectRegistry {
  private plugins = new Map<OverlayEventType, EffectPlugin<OverlayEvent>>()

  /**
   * Регистрирует плагин с учётом версионирования.
   * @returns 'registered' | 'upgraded' | 'skipped'
   */
  register<T extends OverlayEvent>(
    plugin: EffectPlugin<T>
  ): 'registered' | 'upgraded' | 'skipped' {
    const incoming = plugin.version ?? '0.0.0'
    const existing = this.plugins.get(plugin.type)

    if (existing) {
      const current = existing.version ?? '0.0.0'
      if (!isNewerVersion(incoming, current)) {
        return 'skipped'
      }
      this.plugins.set(plugin.type, plugin as unknown as EffectPlugin<OverlayEvent>)
      return 'upgraded'
    }

    this.plugins.set(plugin.type, plugin as unknown as EffectPlugin<OverlayEvent>)
    return 'registered'
  }

  /**
   * Принудительная перезапись без проверки версии.
   * Использовать только для hot-reload в dev-режиме.
   */
  forceRegister<T extends OverlayEvent>(plugin: EffectPlugin<T>): void {
    this.plugins.set(plugin.type, plugin as unknown as EffectPlugin<OverlayEvent>)
  }

  get(type: OverlayEventType): EffectPlugin<OverlayEvent> | undefined {
    return this.plugins.get(type)
  }

  has(type: OverlayEventType): boolean {
    return this.plugins.has(type)
  }

  getAll(): EffectPlugin<OverlayEvent>[] {
    return Array.from(this.plugins.values())
  }

  getVersion(type: OverlayEventType): string {
    return this.plugins.get(type)?.version ?? '0.0.0'
  }

  unregister(type: OverlayEventType): void {
    this.plugins.delete(type)
  }
}

export const effectRegistry = new EffectRegistry()
